/**
 * Custom hook for meeting outcome management
 */
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { apiClient, getErrorMessage } from "../../../utils/api";

export const MEETING_OUTCOMES = [
  { value: "HELD", label: "Održan", description: "+5 bodova", color: "text-green-600" },
  { value: "NO_SHOW", label: "Nije se pojavio", description: "Auto +2 dana", color: "text-amber-600" },
  { value: "RESCHEDULED", label: "Odgođen", description: "Novi termin", color: "text-blue-600" },
  { value: "LOST", label: "Izgubljen", description: "", color: "text-red-600" }
];

export const NEXT_STEP_OPTIONS = [
  { value: "FOLLOW_UP_CALL", label: "Follow-up poziv" },
  { value: "REQUEST_OFFER_BACKOFFICE", label: "Zatražiti ponudu od back-officea" },
  { value: "NEW_MEETING", label: "Novi sastanak" }
];

export const LOST_REASONS = [
  "Cijena previsoka",
  "Odabrao konkurenciju",
  "Nema potrebe",
  "Loše iskustvo",
  "Ostalo"
];

export const useMeetingOutcome = (token, refetch) => {
  const [showOutcomeModal, setShowOutcomeModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [meetingOutcome, setMeetingOutcome] = useState("");
  const [outcomeNote, setOutcomeNote] = useState("");
  const [nextStep, setNextStep] = useState("");
  const [nextActionDate, setNextActionDate] = useState("");
  const [rescheduledDateTime, setRescheduledDateTime] = useState("");
  const [lostReason, setLostReason] = useState("");
  const [savingOutcome, setSavingOutcome] = useState(false);
  const [backofficeEmail, setBackofficeEmail] = useState("backoffice@tvrtka.hr");

  // Fetch backoffice email from settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await apiClient.get('/admin/settings');
        if (response.data.backoffice_email) {
          setBackofficeEmail(response.data.backoffice_email);
        }
      } catch (error) {
        // Silently fail - use default email
        console.log("Could not fetch settings, using default email");
      }
    };

    if (token) {
      fetchSettings();
    }
  }, [token]);

  const handleOutcomeClick = (activity) => {
    setSelectedMeeting(activity);
    setMeetingOutcome("");
    setOutcomeNote("");
    setNextStep("");
    setNextActionDate("");
    setRescheduledDateTime("");
    setLostReason("");
    setShowOutcomeModal(true);
  };

  const handleSaveOutcome = async () => {
    if (!meetingOutcome) {
      toast.error("Odaberite ishod sastanka");
      return;
    }

    // Validation
    if (meetingOutcome === "HELD") {
      if (!nextStep) {
        toast.error("Odaberite sljedeći korak");
        return;
      }
      if (nextStep !== "NEW_MEETING" && !nextActionDate) {
        toast.error("Datum sljedeće akcije je obavezan");
        return;
      }
      if (nextStep === "NEW_MEETING" && !rescheduledDateTime && !nextActionDate) {
        toast.error("Datum novog sastanka ili sljedeće akcije je obavezan");
        return;
      }
    }

    if (meetingOutcome === "RESCHEDULED" && !rescheduledDateTime) {
      toast.error("Novi datum sastanka je obavezan");
      return;
    }

    setSavingOutcome(true);
    try {
      const payload = {
        meeting_outcome: meetingOutcome,
        note: outcomeNote || null,
        next_step: nextStep || null,
        next_action_date: nextActionDate || null,
        rescheduled_datetime: rescheduledDateTime || null,
        lost_reason: lostReason || null
      };

      await apiClient.patch(`/activities/${selectedMeeting.id}/meeting-outcome`, payload);

      toast.success("Ishod sastanka spremljen");
      setShowOutcomeModal(false);
      refetch();
    } catch (error) {
      console.error("Failed to save outcome:", error);
      toast.error(getErrorMessage(error));
    } finally {
      setSavingOutcome(false);
    }
  };

  return {
    showOutcomeModal,
    setShowOutcomeModal,
    selectedMeeting,
    meetingOutcome,
    setMeetingOutcome,
    outcomeNote,
    setOutcomeNote,
    nextStep,
    setNextStep,
    nextActionDate,
    setNextActionDate,
    rescheduledDateTime,
    setRescheduledDateTime,
    lostReason,
    setLostReason,
    savingOutcome,
    backofficeEmail,
    handleOutcomeClick,
    handleSaveOutcome
  };
};
